import { AccountStatus, FitTier, LeadAccountDto } from "@growth/dto";
import { Badge, Button, cn, Input, PromptDialog } from "@growth/ui";
import {
  ArrowLeft,
  Check,
  Clock,
  ExternalLink,
  FileUp,
  Globe,
  Landmark,
  LoaderCircle,
  Network,
  Pause,
  Search,
  X,
} from "lucide-react";
import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { ErrorText, PageHeader, selectClass } from "../../components/page-header";
import { useActive } from "../../lib/active";
import { apiErrorMessage } from "../../lib/api-error";
import { formatDate } from "../../lib/format";
import { useIcp } from "../../lib/icp";
import {
  useFindAccounts,
  useLeadAccount,
  useLeadAccounts,
  useSetAccountStatus,
} from "../../lib/leads";
import { WEIGHT_LABEL } from "./icp";
import {
  BestChannel,
  card,
  CheckDots,
  ContactStatusBadge,
  FitBar,
  Gate,
  Panel,
  ROLE_LABEL,
  SampleBanner,
  stageLabel,
  TierBadge,
  VerdictIcon,
} from "./shared";

const STATUS: Record<AccountStatus, { label: string; className: string }> = {
  suggested: { label: "To review", className: "bg-amber-50 text-amber-800 ring-amber-200" },
  approved: { label: "Approved", className: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  held: { label: "On hold", className: "bg-paper text-ink ring-line" },
  excluded: { label: "Excluded", className: "bg-red-50 text-red-700 ring-red-200" },
};

const SOURCE_LABEL: Record<LeadAccountDto["source"], string> = {
  research: "Our research",
  register: "Public register",
  csv: "Your list",
  gateway: "Data provider",
  legacy: "Old Leads page",
};

const TABS: { id: AccountStatus | "all"; label: string }[] = [
  { id: "all", label: "All" },
  { id: "suggested", label: "To review" },
  { id: "approved", label: "Approved" },
  { id: "held", label: "On hold" },
  { id: "excluded", label: "Excluded" },
];

export const AccountsPage = () => {
  const { brand, atLeast } = useActive();
  const icp = useIcp();
  const accounts = useLeadAccounts();
  const [params, setParams] = useSearchParams();
  const status = (params.get("status") as AccountStatus | null) ?? "all";
  const [segment, setSegment] = useState("all");
  const [tier, setTier] = useState<FitTier | "all">("all");
  const [query, setQuery] = useState("");
  const [finding, setFinding] = useState(false);

  const rows = (accounts.data ?? []).filter(
    (account) =>
      (status === "all" || account.status === status) &&
      (segment === "all" || account.segmentId === segment) &&
      (tier === "all" || account.fitTier === tier) &&
      account.name.toLowerCase().includes(query.trim().toLowerCase()),
  );
  const count = (id: AccountStatus | "all") =>
    (accounts.data ?? []).filter((account) => id === "all" || account.status === id).length;

  return (
    <section className="max-w-6xl space-y-6">
      <SampleBanner />
      <PageHeader
        eyebrow={`${brand?.name ?? ""} · Leads`}
        title="Accounts"
        actions={
          icp.data?.inUse && atLeast("EDITOR") ? (
            <Button variant="secondary" onClick={() => setFinding(!finding)}>
              <Search className="size-4" /> Find more accounts
            </Button>
          ) : undefined
        }
      >
        Organisations judged against ICP v{icp.data?.inUse?.version ?? "–"}. Each one shows the
        proof behind every check, so you can see why it fits before anyone is contacted.
      </PageHeader>
      {(accounts.error ?? icp.error) && (
        <ErrorText>{apiErrorMessage(accounts.error ?? icp.error)}</ErrorText>
      )}
      {(accounts.isPending || icp.isPending) && (
        <LoaderCircle className="size-5 animate-spin text-muted" aria-label="Loading" />
      )}
      {icp.data && !icp.data.inUse ? (
        <Gate
          title="Confirm the ideal customer profile first"
          to="/leads/icp"
          action="Go to the ICP"
        >
          Accounts are found and judged against the ICP in use. Once you've confirmed one, the
          agents can start finding accounts and checking each one for proof.
        </Gate>
      ) : (
        accounts.data && (
          <>
            {finding && <FindPanel onClose={() => setFinding(false)} />}
            <Behind
              version={icp.data?.inUse?.version ?? 0}
              behind={accounts.data.filter(
                (account) =>
                  account.status !== "excluded" &&
                  account.icpVersion < (icp.data?.inUse?.version ?? 0),
              )}
            />
            <div className="flex flex-wrap items-center gap-2">
              <div
                role="tablist"
                aria-label="Status"
                className="flex flex-wrap gap-1 rounded-lg bg-paper p-1"
              >
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={status === tab.id}
                    onClick={() =>
                      setParams(tab.id === "all" ? {} : { status: tab.id }, { replace: true })
                    }
                    className={cn(
                      "rounded-md px-3 py-1 text-xs font-medium",
                      status === tab.id
                        ? "bg-white text-ink shadow-sm"
                        : "text-muted hover:text-ink",
                    )}
                  >
                    {tab.label} <span className="tabular-nums text-muted">{count(tab.id)}</span>
                  </button>
                ))}
              </div>
              <select
                aria-label="Segment"
                className={cn(selectClass, "w-auto max-w-[14rem]")}
                value={segment}
                onChange={(event) => setSegment(event.target.value)}
              >
                <option value="all">All segments</option>
                {[
                  ...new Map(
                    accounts.data.map((account) => [account.segmentId, account.segmentName]),
                  ),
                ].map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
              <select
                aria-label="Fit tier"
                className={cn(selectClass, "w-auto")}
                value={tier}
                onChange={(event) => setTier(event.target.value as FitTier | "all")}
              >
                <option value="all">All tiers</option>
                {(["A", "B", "C", "D"] as const).map((item) => (
                  <option key={item} value={item}>
                    Tier {item}
                  </option>
                ))}
              </select>
              <Input
                aria-label="Search accounts"
                placeholder="Search"
                className="w-48"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>
            <AccountTable rows={rows} />
          </>
        )
      )}
    </section>
  );
};

/** A new ICP version changes the checks; accounts judged against an older one say so. */
const Behind = ({ version, behind }: { version: number; behind: LeadAccountDto[] }) => {
  if (!behind.length) return null;
  const old = Math.min(...behind.map((account) => account.icpVersion));
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-ink">
      <span>
        {behind.length} account{behind.length === 1 ? " was" : "s were"} judged against v{old}. The
        agents re-check {behind.length === 1 ? "it" : "them"} against v{version} and show you any
        that moved tier before anything changes.
      </span>
      <Button variant="secondary" disabled title="Arrives with the next phase">
        Re-check against v{version}
      </Button>
    </div>
  );
};

const AccountTable = ({ rows }: { rows: LeadAccountDto[] }) => {
  const { atLeast } = useActive();
  const setStatus = useSetAccountStatus();
  if (!rows.length)
    return <p className={cn(card, "p-6 text-sm text-muted")}>No accounts match these filters.</p>;
  return (
    <div className={cn(card, "overflow-x-auto")}>
      <table className="w-full min-w-[40rem] text-left text-sm">
        <thead className="border-b border-line bg-paper/60 text-[11px] uppercase tracking-wider text-muted">
          <tr>
            <th className="px-4 py-2 font-semibold">Account</th>
            <th className="px-3 py-2 font-semibold">Fit</th>
            <th className="px-3 py-2 font-semibold">Checks</th>
            <th className="px-3 py-2 font-semibold">People</th>
            <th className="px-3 py-2 font-semibold">Status</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {rows.map((account) => (
            <tr key={account.id} className="align-top hover:bg-paper/30">
              <td className="min-w-[16rem] px-4 py-3">
                <Link
                  to={`/leads/accounts/${account.id}`}
                  className="font-medium text-ink hover:underline"
                >
                  {account.name}
                </Link>
                <p className="text-xs text-muted">
                  {account.segmentName} · {account.location}
                </p>
                {account.signal && (
                  <p className="mt-1 inline-flex items-center gap-1 rounded bg-blue-50 px-1.5 py-0.5 text-[11px] text-accent">
                    <Clock className="size-3" /> {account.signal}
                  </p>
                )}
              </td>
              <td className="px-3 py-3">
                <span className="flex items-center gap-2">
                  <TierBadge tier={account.fitTier} />
                  <FitBar score={account.fitScore} max={account.fitMax} />
                </span>
              </td>
              <td className="px-3 py-3">
                <CheckDots counts={account.checkCounts} />
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-xs tabular-nums text-muted">
                {account.people}
                {account.people > 0 && ` · ${account.emailable} by email`}
              </td>
              <td className="px-3 py-3">
                <Badge className={cn("whitespace-nowrap", STATUS[account.status].className)}>
                  {STATUS[account.status].label}
                </Badge>
              </td>
              <td className="px-3 py-3 text-right">
                {account.status === "suggested" && atLeast("EDITOR") && (
                  <span className="inline-flex gap-1">
                    <Button
                      variant="secondary"
                      className="h-8 px-2"
                      disabled={setStatus.isPending}
                      onClick={() =>
                        setStatus.mutate({ id: account.id, status: "approved", note: "" })
                      }
                    >
                      <Check className="size-4" /> Approve
                    </Button>
                    <Button
                      variant="ghost"
                      className="h-8 px-2"
                      aria-label={`Exclude ${account.name}`}
                      disabled={setStatus.isPending}
                      onClick={() =>
                        setStatus.mutate({ id: account.id, status: "excluded", note: "Not a fit" })
                      }
                    >
                      <X className="size-4" />
                    </Button>
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SOURCES = [
  {
    id: "research",
    icon: Globe,
    label: "Our web research",
    about:
      "The researcher searches the web and reads each site. About US$0.40 an account, with its people.",
  },
  {
    id: "register",
    icon: Landmark,
    label: "A public register",
    about:
      "Import from an official list (TEQSA, the NDIS Provider Register, ABN Lookup), then check each one.",
  },
  {
    id: "csv",
    icon: FileUp,
    label: "Your own list",
    about: "Upload a CSV from your CRM, an event or a Sales Navigator export done by a person.",
  },
  {
    id: "gateway",
    icon: Network,
    label: "A data provider",
    about: "Company search through the data gateway, paid per call from this brand's leads budget.",
  },
] as const;

const FindPanel = ({ onClose }: { onClose: () => void }) => {
  const find = useFindAccounts();
  const [source, setSource] = useState<(typeof SOURCES)[number]["id"]>("research");
  return (
    <Panel
      title="Find more accounts"
      about="New accounts arrive as “To review”. Nothing is contacted until a person approves the account and the email."
      actions={
        <Button variant="ghost" onClick={onClose} aria-label="Close">
          <X className="size-4" />
        </Button>
      }
    >
      <div role="radiogroup" aria-label="Where to look" className="grid gap-2 sm:grid-cols-2">
        {SOURCES.map(({ id, icon: Icon, label, about }) => (
          <button
            key={id}
            type="button"
            role="radio"
            aria-checked={source === id}
            onClick={() => setSource(id)}
            className={cn(
              "flex gap-3 rounded-lg border p-3 text-left",
              source === id ? "border-accent bg-blue-50/40" : "border-line hover:bg-paper/40",
            )}
          >
            <Icon className="mt-0.5 size-4 shrink-0 text-muted" />
            <span>
              <span className="block text-sm font-medium text-ink">{label}</span>
              <span className="block text-xs text-muted">{about}</span>
            </span>
          </button>
        ))}
      </div>
      {find.isError && <ErrorText>{apiErrorMessage(find.error)}</ErrorText>}
      {find.isSuccess && (
        <p className="text-sm text-emerald-700">
          Added {find.data} account{find.data === 1 ? "" : "s"} to review.
        </p>
      )}
      <div className="flex items-center gap-3">
        <Button disabled={find.isPending} onClick={() => find.mutate()}>
          {find.isPending ? (
            <LoaderCircle className="size-4 animate-spin" />
          ) : (
            <Search className="size-4" />
          )}
          Find accounts
        </Button>
        <span className="text-xs text-muted">
          In the sample, every source adds the same two made-up accounts.
        </span>
      </div>
    </Panel>
  );
};

// ─── One account ─────────────────────────────────────────────────────────────────────────────

export const AccountPage = () => {
  const { id = "" } = useParams();
  const { atLeast } = useActive();
  const account = useLeadAccount(id);
  const setStatus = useSetAccountStatus();
  const [asking, setAsking] = useState<"held" | "excluded" | null>(null);
  const data = account.data;

  return (
    <section className="max-w-6xl space-y-6">
      <SampleBanner />
      <Link
        to="/leads/accounts"
        className="inline-flex items-center gap-1 text-sm text-muted hover:text-ink"
      >
        <ArrowLeft className="size-4" /> Accounts
      </Link>
      {account.isError && <ErrorText>{apiErrorMessage(account.error)}</ErrorText>}
      {account.isPending && (
        <LoaderCircle className="size-5 animate-spin text-muted" aria-label="Loading" />
      )}
      {data && (
        <>
          <header className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted">
                {data.segmentName} · {data.location}
              </p>
              <h1 className="flex items-center gap-3 font-serif text-3xl text-ink">
                <TierBadge tier={data.fitTier} className="size-8 text-lg" />
                {data.name}
              </h1>
              <p className="flex flex-wrap items-center gap-3 text-sm">
                <a
                  href={data.website}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-accent underline"
                >
                  {new URL(data.website).hostname} <ExternalLink className="size-3" />
                </a>
                <Badge className={STATUS[data.status].className}>{STATUS[data.status].label}</Badge>
              </p>
            </div>
            {atLeast("EDITOR") && (
              <div className="flex flex-wrap gap-2">
                {data.status !== "approved" && (
                  <Button
                    disabled={setStatus.isPending}
                    onClick={() => setStatus.mutate({ id: data.id, status: "approved", note: "" })}
                  >
                    <Check className="size-4" /> Approve
                  </Button>
                )}
                {data.status !== "held" && (
                  <Button variant="secondary" onClick={() => setAsking("held")}>
                    <Pause className="size-4" /> Hold
                  </Button>
                )}
                {data.status !== "excluded" && (
                  <Button variant="ghost" onClick={() => setAsking("excluded")}>
                    <X className="size-4" /> Exclude
                  </Button>
                )}
              </div>
            )}
          </header>
          {(data.status === "held" || data.status === "excluded") && data.statusNote && (
            <p className="rounded-lg bg-paper px-4 py-2 text-sm text-ink">
              {data.status === "held" ? "On hold" : "Excluded"}: {data.statusNote} Nobody here can
              be contacted while it stays this way.
            </p>
          )}
          {setStatus.isError && <ErrorText>{apiErrorMessage(setStatus.error)}</ErrorText>}

          <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_18rem]">
            <div className="min-w-0 space-y-4">
              <Panel
                title="Why it fits"
                about={`${data.fitScore} of ${data.fitMax} check points · tier ${data.fitTier}. Unknown is not a fail.`}
              >
                <ul className="divide-y divide-line">
                  {data.checks.map((item) => (
                    <li
                      key={item.checkId}
                      className="grid grid-cols-[1.25rem_minmax(0,1fr)] gap-3 py-3"
                    >
                      <VerdictIcon verdict={item.verdict} />
                      <div className="min-w-0 space-y-1">
                        <p className="flex flex-wrap items-baseline justify-between gap-2 text-sm text-ink">
                          {item.text}
                          <span className="text-xs text-muted">
                            {WEIGHT_LABEL[item.weight]} · {item.weight}
                          </span>
                        </p>
                        {item.quote ? (
                          <blockquote className="border-l-2 border-amber-300 bg-amber-50/50 px-3 py-1 text-sm text-ink">
                            “{item.quote}”
                          </blockquote>
                        ) : (
                          <p className="text-xs text-muted">No proof found either way yet.</p>
                        )}
                        {item.url && (
                          <p className="break-all text-xs text-muted">
                            <a
                              href={item.url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-accent underline"
                            >
                              {item.url.replace(/^https?:\/\//, "")}
                            </a>
                            {item.seenAt && ` · seen ${formatDate(item.seenAt)}`}
                          </p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </Panel>

              <Panel
                title="People"
                about="Found on the account's own pages. Each shows whether and how we may contact them."
              >
                {data.people.length === 0 ? (
                  <p className="text-sm text-muted">Nobody found yet.</p>
                ) : (
                  <ul className="divide-y divide-line">
                    {data.people.map((person) => (
                      <li
                        key={person.id}
                        className="flex flex-wrap items-center justify-between gap-3 py-3"
                      >
                        <div className="min-w-0">
                          <Link
                            to={`/leads/people/${person.id}`}
                            className="font-medium text-ink hover:underline"
                          >
                            {person.name}
                          </Link>
                          <p className="text-xs text-muted">
                            {person.title} · {ROLE_LABEL[person.role]}
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-3">
                          <ContactStatusBadge status={person.contactStatus} />
                          <BestChannel channel={person.bestChannel} />
                          <span className="text-xs text-muted">{stageLabel(person.stage)}</span>
                          <span
                            className="w-10 text-right text-sm tabular-nums text-ink"
                            title="Score"
                          >
                            {person.engagement.score}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>
            </div>

            <aside className="space-y-4">
              <div className={cn(card, "space-y-3 p-4 text-sm")}>
                <h2 className="text-xs font-semibold uppercase tracking-widest text-muted">
                  Timing
                </h2>
                {data.signals.length === 0 ? (
                  <p className="text-muted">No signals yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {data.signals.map((signal) => (
                      <li key={signal.url + signal.text}>
                        <a
                          href={signal.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-ink hover:underline"
                        >
                          {signal.text}
                        </a>
                        <span className="block text-xs text-muted">
                          {formatDate(signal.seenAt)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className={cn(card, "space-y-3 p-4 text-sm")}>
                <h2 className="text-xs font-semibold uppercase tracking-widest text-muted">
                  Contact routes
                </h2>
                <dl className="space-y-2">
                  <div>
                    <dt className="text-xs text-muted">Team inbox</dt>
                    <dd className="break-all text-ink">{data.teamInbox ?? "None published"}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted">Phone</dt>
                    <dd className="text-ink">{data.phone ?? "None published"}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted">Email pattern</dt>
                    <dd className="break-all text-ink">
                      {data.emailPattern ? data.emailPattern.pattern : "Not known"}
                    </dd>
                    {data.emailPattern && (
                      <dd className="text-xs text-muted">
                        From {data.emailPattern.basedOn} published addresses. Used to check
                        addresses, never to cold email someone in Australia.
                      </dd>
                    )}
                  </div>
                </dl>
              </div>
              <p className="text-xs text-muted">
                {SOURCE_LABEL[data.source]}, found {formatDate(data.foundAt)}. Research cost US$
                {data.researchCostUsd.toFixed(2)}.
              </p>
            </aside>
          </div>
          <PromptDialog
            open={asking !== null}
            onOpenChange={(open) => !open && setAsking(null)}
            title={asking === "held" ? `Hold ${data.name}` : `Exclude ${data.name}`}
            label="Why? (shown to everyone on the team)"
            placeholder={
              asking === "held" ? "e.g. Parent of a current customer" : "e.g. No careers service"
            }
            submitLabel={asking === "held" ? "Hold" : "Exclude"}
            onSubmit={(value) => {
              if (value.length < 3) return "Say why in a few words.";
              setStatus.mutate({ id: data.id, status: asking!, note: value });
              return null;
            }}
          />
        </>
      )}
    </section>
  );
};
